import { useEffect, useMemo, useState } from "react";
import api, { fmtIDR } from "@/lib_app/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Target, Save, Plus, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/app/Avatar";
import MonthlyTargetModal from "@/components/app/MonthlyTargetModal";
import NumberInput from "@/components/app/NumberInput";

const currentYear = new Date().getFullYear();

export default function Targets() {
  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState([]);
  const [dbYears, setDbYears] = useState([]);
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [newYear, setNewYear] = useState(String(currentYear + 1));
  const [copyEnabled, setCopyEnabled] = useState(true);
  const [copyFromYear, setCopyFromYear] = useState(String(currentYear));
  const [monthlyFor, setMonthlyFor] = useState(null);
  const [rawTargets, setRawTargets] = useState({}); // includes overrides

  const loadYears = async () => {
    try {
      const r = await api.get("/targets/years");
      setDbYears(r.data.years || []);
      const stored = JSON.parse(localStorage.getItem("sp_target_years") || "[]");
      const combined = Array.from(new Set([
        currentYear - 1, currentYear, currentYear + 1,
        ...(r.data.years || []),
        ...stored,
      ])).sort((a, b) => a - b);
      setYears(combined);
    } catch {
      setYears([currentYear - 1, currentYear, currentYear + 1]);
    }
  };

  const load = async () => {
    const [u, t] = await Promise.all([
      api.get("/users"),
      api.get(`/targets?year=${year}`),
    ]);
    // Owner is a super admin — excluded from target management
    const nonOwner = (u.data || []).filter((usr) => usr.role !== "owner" && usr.role !== "finance");
    setUsers(nonOwner);
    const tmap = {};
    const rmap = {};
    (t.data || []).forEach((x) => {
      tmap[x.user_id] = x.annual_target;
      rmap[x.user_id] = x;
    });
    setTargets(tmap);
    setRawTargets(rmap);
    const d = {};
    nonOwner.forEach((usr) => { d[usr.user_id] = String(tmap[usr.user_id] || 0); });
    setDrafts(d);
  };

  useEffect(() => { loadYears(); }, []);
  useEffect(() => { load(); }, [year]);

  const save = async (user_id) => {
    const val = Number(drafts[user_id] || 0);
    setBusy((b) => ({ ...b, [user_id]: true }));
    try {
      await api.put(`/targets/${user_id}`, { year, annual_target: val });
      toast.success("Target diperbarui");
      loadYears();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally {
      setBusy((b) => ({ ...b, [user_id]: false }));
    }
  };

  const submitAddYear = async (e) => {
    e.preventDefault();
    const y = Number(newYear);
    if (!y || y < 2000 || y > 2100) {
      return toast.error("Masukkan tahun yang valid (2000-2100)");
    }
    if (years.includes(y) && y !== year) {
      toast.info("Tahun sudah tersedia");
      setYear(y);
      setAddOpen(false);
      return;
    }
    const stored = JSON.parse(localStorage.getItem("sp_target_years") || "[]");
    if (!stored.includes(y)) {
      stored.push(y);
      localStorage.setItem("sp_target_years", JSON.stringify(stored));
    }
    setYears((prev) => Array.from(new Set([...prev, y])).sort((a, b) => a - b));

    // Copy targets from source year if enabled
    let copied = 0;
    if (copyEnabled) {
      const from = Number(copyFromYear);
      if (from && from !== y) {
        try {
          const r = await api.post("/targets/copy", { from_year: from, to_year: y });
          copied = r.data.copied || 0;
        } catch (err) {
          toast.error(err.response?.data?.detail || "Gagal salin target");
        }
      }
    }
    setYear(y);
    setAddOpen(false);
    if (copied > 0) {
      toast.success(`Tahun ${y} ditambahkan. ${copied} target disalin dari ${copyFromYear}.`);
    } else {
      toast.success(`Tahun ${y} ditambahkan. Silakan atur target di bawah.`);
    }
    loadYears();
  };

  const deleteYear = async (y) => {
    const hasDbData = dbYears.includes(y);
    const msg = hasDbData
      ? `Hapus semua target tahun ${y}? Data ${users.length} sales untuk tahun ini akan hilang.`
      : `Hapus tahun ${y} dari daftar?`;
    if (!window.confirm(msg)) return;
    try {
      if (hasDbData) {
        await api.delete(`/targets/year/${y}`);
      }
      // remove from localStorage extras
      const stored = JSON.parse(localStorage.getItem("sp_target_years") || "[]");
      localStorage.setItem("sp_target_years", JSON.stringify(stored.filter((x) => x !== y)));
      toast.success(`Tahun ${y} dihapus`);
      if (year === y) setYear(currentYear);
      loadYears();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal hapus");
    }
  };

  const canDeleteYear = (y) => {
    // Don't allow deleting the year currently being edited (safety)
    // Protect current year always
    return y !== currentYear;
  };

  const totalAnnual = useMemo(() => Object.values(targets).reduce((s, v) => s + Number(v || 0), 0), [targets]);
  const totalMonthly = Math.round(totalAnnual / 12);

  return (
    <div className="space-y-6" data-testid="targets-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Manajemen</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Target Sales</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Atur target tahunan per anggota tim. Target bulanan otomatis = tahunan ÷ 12.</p>
        </div>
        <div className="flex gap-2.5">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="target-year" className="w-40 h-11 rounded-full bg-white card-soft px-5 border-transparent"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <div key={y} className="relative flex items-center group">
                  <SelectItem value={String(y)} className="flex-1 pr-9">Tahun {y}</SelectItem>
                  {canDeleteYear(y) && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteYear(y); }}
                      data-testid={`delete-year-${y}`}
                      title={`Hapus tahun ${y}`}
                      className="absolute right-2 w-6 h-6 rounded-md inline-flex items-center justify-center text-[#A0A3A8] hover:bg-red-50 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => {
              const last = years[years.length - 1] || currentYear;
              setNewYear(String(last + 1));
              setCopyFromYear(String(last));
              setCopyEnabled(dbYears.length > 0);
              setAddOpen(true);
            }}
            data-testid="add-year-btn"
            className="gap-2 h-11 rounded-full bg-[#090808] hover:bg-[#000000] px-5 shadow-[0_6px_16px_rgba(9,8,8,0.25)]"
          >
            <Plus className="w-4 h-4" /> Tambah Tahun
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card
          className="p-6 border-0 rounded-3xl overflow-hidden relative"
          style={{
            background:
              "radial-gradient(500px 350px at 100% 0%, rgba(107,143,226,0.32) 0%, transparent 60%)," +
              "radial-gradient(400px 300px at 0% 100%, rgba(151,173,212,0.24) 0%, transparent 60%)," +
              "linear-gradient(135deg, rgba(255,255,255,0.80) 0%, rgba(232,240,254,0.68) 100%)",
            backdropFilter: "blur(24px) saturate(170%)",
            WebkitBackdropFilter: "blur(24px) saturate(170%)",
            border: "1px solid rgba(255,255,255,0.75)",
            boxShadow: "0 22px 46px -18px rgba(30,64,175,0.18), 0 6px 20px -10px rgba(20,22,31,0.08)",
          }}
        >
          <div className="flex items-start justify-between mb-4 relative z-10">
            <span className="text-xs uppercase tracking-widest text-[#626061] font-semibold">Total Target Tahunan {year}</span>
            <div className="w-9 h-9 rounded-xl bg-white/70 text-[#4E6D9A] flex items-center justify-center">
              <Target className="w-4 h-4" strokeWidth={1.75} />
            </div>
          </div>
          <div className="text-3xl font-num font-bold text-[#090808] relative z-10">{fmtIDR(totalAnnual)}</div>
        </Card>
        <Card
          className="p-6 border-0 rounded-3xl overflow-hidden relative"
          style={{
            background:
              "radial-gradient(500px 350px at 100% 0%, rgba(107,143,226,0.32) 0%, transparent 60%)," +
              "radial-gradient(400px 300px at 0% 100%, rgba(151,173,212,0.24) 0%, transparent 60%)," +
              "linear-gradient(135deg, rgba(255,255,255,0.80) 0%, rgba(232,240,254,0.68) 100%)",
            backdropFilter: "blur(24px) saturate(170%)",
            WebkitBackdropFilter: "blur(24px) saturate(170%)",
            border: "1px solid rgba(255,255,255,0.75)",
            boxShadow: "0 22px 46px -18px rgba(30,64,175,0.18), 0 6px 20px -10px rgba(20,22,31,0.08)",
          }}
        >
          <div className="flex items-start justify-between mb-4 relative z-10">
            <span className="text-xs uppercase tracking-widest text-[#626061] font-semibold">Target Bulanan (rata-rata)</span>
            <div className="w-9 h-9 rounded-xl bg-white/70 text-[#4E6D9A] flex items-center justify-center">
              <Target className="w-4 h-4" strokeWidth={1.75} />
            </div>
          </div>
          <div className="text-3xl font-num font-bold text-[#090808] relative z-10">{fmtIDR(totalMonthly)}</div>
        </Card>
      </div>

      <Card className="border-0 rounded-2xl card-soft overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">
            <tr>
              <th className="px-6 py-4 text-left">Sales</th>
              <th className="px-6 py-4 text-right">Target Tahunan</th>
              <th className="px-6 py-4 text-right">Target Bulanan</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`target-row-${u.user_id}`} className="border-t border-[#DCE0E7]">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Avatar name={u.name} picture={u.picture} size="sm" />
                    <div>
                      <div className="font-semibold text-[#090808]">{u.name}</div>
                      <div className="text-xs text-[#A0A3A8]">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <NumberInput
                    data-testid={`target-input-${u.user_id}`}
                    value={drafts[u.user_id] || ""}
                    onChange={(v) => setDrafts({ ...drafts, [u.user_id]: v })}
                    className="w-56 ml-auto text-right font-num"
                    placeholder="0"
                  />
                </td>
                <td className="px-6 py-4 text-right font-num text-sm text-[#A0A3A8] tabular-nums">
                  {fmtIDR(Math.round((Number(drafts[u.user_id] || 0)) / 12))}
                  {rawTargets[u.user_id]?.monthly_overrides?.some((v) => v) && (
                    <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-[#DCE0E7] text-[#626061] font-semibold uppercase tracking-wider">Custom</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMonthlyFor(u)}
                      data-testid={`monthly-btn-${u.user_id}`}
                      className="rounded-full h-9 gap-1.5 border-[#DCE0E7]"
                      size="sm"
                    >
                      <CalendarDays className="w-3.5 h-3.5" /> Per Bulan
                    </Button>
                    <Button
                      onClick={() => save(u.user_id)}
                      disabled={busy[u.user_id] || String(drafts[u.user_id] || 0) === String(targets[u.user_id] || 0)}
                      data-testid={`target-save-${u.user_id}`}
                      className="bg-[#090808] hover:bg-[#000000] rounded-full h-9 gap-1.5"
                      size="sm"
                    >
                      <Save className="w-3.5 h-3.5" /> Simpan
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="add-year-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#090808]">Tambah Tahun</DialogTitle>
            <DialogDescription>Tahun akan tersedia di dropdown dan siap diisi target per sales.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAddYear} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8]">Tahun</Label>
              <Input
                type="number"
                min="2000"
                max="2100"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                data-testid="new-year-input"
                className="text-lg font-num font-semibold text-center h-12"
                autoFocus
                required
              />
            </div>

            {dbYears.length > 0 && (
              <div className="rounded-xl border border-[#DCE0E7] p-3 space-y-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={copyEnabled}
                    onChange={(e) => setCopyEnabled(e.target.checked)}
                    data-testid="copy-enabled-checkbox"
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-[#090808]">Salin target dari tahun sebelumnya</span>
                </label>
                {copyEnabled && (
                  <Select value={copyFromYear} onValueChange={setCopyFromYear}>
                    <SelectTrigger data-testid="copy-from-year" className="w-full h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {dbYears.map((y) => <SelectItem key={y} value={String(y)}>Tahun {y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-[11px] text-[#A0A3A8]">
                  Nilai target per sales akan disalin. Kamu tetap bisa edit setelah dibuat.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="add-year-submit" className="bg-[#090808] hover:bg-[#000000] rounded-full gap-1.5">
                <Plus className="w-4 h-4" /> Tambah
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MonthlyTargetModal
        open={!!monthlyFor}
        onOpenChange={(o) => !o && setMonthlyFor(null)}
        userRow={monthlyFor}
        year={year}
        annualTarget={monthlyFor ? (targets[monthlyFor.user_id] || 0) : 0}
        existingOverrides={monthlyFor ? (rawTargets[monthlyFor.user_id]?.monthly_overrides || null) : null}
        onSaved={load}
      />
    </div>
  );
}
